import { useEffect, useRef, useState } from 'react';

const QUICK_REPLIES = ['What do you build?', 'How much does a project cost?', 'How fast can you start?', 'I want to talk to a human'];

const RULES = [
  {
    match: /(what|services|build|do you (do|offer))/i,
    reply: "We're an AI-first agency — we build AI agents & LLM automation, Web3/blockchain systems, full-stack SaaS, DevOps infrastructure, PropTech platforms, and self-sovereign identity systems. Anything specific on your mind?",
  },
  {
    match: /(cost|price|pricing|budget|rate|charge|much)/i,
    reply: "It depends on scope — we quote architecture, timeline, and cost upfront before any commitment. MVPs typically start around 4–6 weeks of build time. Share your idea at connect@spreadcode.org and we'll send a concrete estimate within 2 hours.",
  },
  {
    match: /(how (fast|soon|quick)|start|timeline|when|available)/i,
    reply: "We usually respond within 2 hours and can typically kick off discovery within a few days. Recent MVPs went from wireframe to live product in 6 weeks.",
  },
  {
    match: /(ai|agent|llm|gpt|claude|automation|chatbot)/i,
    reply: "AI is our core strength — LLM-powered agents that triage, reason, and act. We've built support platforms that cut ticket volume by 68% using LangChain, OpenAI, and custom agent frameworks. What workflow do you want to automate?",
  },
  {
    match: /(web3|blockchain|defi|nft|smart contract|solidity|crypto)/i,
    reply: "We build production Web3: DeFi protocols, multi-chain trackers, NFT platforms. Our ChainVault project spans 12 networks and has processed $2M+ without a single exploit. Solidity, Ethers.js, The Graph — battle-tested.",
  },
  {
    match: /(human|person|talk|call|email|contact|reach)/i,
    reply: "Of course! Email us at connect@spreadcode.org or hit the button below — Azaz personally replies within 2 hours.",
    cta: true,
  },
  {
    match: /(who|team|about|review)/i,
    reply: "SpreadCode is an AI-first engineering agency out of Islamabad, working worldwide. 20,000+ hours engineered across AI agents, Web3, and SaaS.",
  },
  {
    match: /(hi|hello|hey|salam|assalam)/i,
    reply: "Hey! 👋 Great to meet you. Ask me anything about what we build, pricing, timelines — or just tell me about your project idea.",
  },
];

const FALLBACK = "That sounds interesting! For anything specific, the fastest path is emailing connect@spreadcode.org — a human replies within 2 hours. Meanwhile, try asking me about our services, pricing, or timelines.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hi, I'm Spread — SpreadCode's AI assistant. 🤖 Ask me what we build, what it costs, or how fast we ship. Or just describe your project!" },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [unread, setUnread] = useState(true);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, typing, open]);

  const send = text => {
    const q = text.trim();
    if (!q) return;
    setMessages(m => [...m, { from: 'user', text: q }]);
    setInput('');
    setTyping(true);
    const rule = RULES.find(r => r.match.test(q));
    const reply = rule ? rule.reply : FALLBACK;
    const cta = rule?.cta || !rule;
    setTimeout(() => {
      setTyping(false);
      setMessages(m => [...m, { from: 'bot', text: reply, cta }]);
    }, 700 + Math.min(reply.length * 6, 1100));
  };

  return (
    <>
      <button
        className={`chat-fab${open ? ' chat-fab--open' : ''}`}
        onClick={() => { setOpen(o => !o); setUnread(false); }}
        aria-label="Chat with us"
      >
        {open ? '✕' : '✳'}
        {!open && unread && <span className="chat-fab-badge" />}
      </button>

      <div className={`chat-panel${open ? ' chat-panel--open' : ''}`}>
        <div className="chat-head">
          <div className="chat-avatar">✳</div>
          <div>
            <div className="chat-name">Spread</div>
            <div className="chat-status"><span className="chat-dot" /> AI assistant · always online</div>
          </div>
        </div>

        <div className="chat-body" ref={bodyRef}>
          {messages.map((m, i) => (
            <div key={i} className={`chat-msg chat-msg--${m.from}`}>
              {m.text}
              {m.cta && (
                <a className="chat-cta" href="mailto:connect@spreadcode.org?subject=Project Inquiry via Chat">
                  Email us directly →
                </a>
              )}
            </div>
          ))}
          {typing && (
            <div className="chat-msg chat-msg--bot chat-typing">
              <span /><span /><span />
            </div>
          )}
        </div>

        <div className="chat-quick">
          {QUICK_REPLIES.map(q => (
            <button key={q} className="chat-chip" onClick={() => send(q)}>{q}</button>
          ))}
        </div>

        <div className="chat-input-row">
          <input
            className="chat-input"
            value={input}
            placeholder="Ask about your project..."
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send(input)}
            aria-label="Chat message"
          />
          <button className="chat-send" onClick={() => send(input)} aria-label="Send">→</button>
        </div>
      </div>
    </>
  );
}
